import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCurrency, formatDate, posterSrc } from "../lib/api";
import { Ticket, Calendar, MapPin, Bell } from "lucide-react";

const STATUS = {
  refund_pending: { label: "Cancelled · refund in progress", cls: "bg-yellow-500/15 text-yellow-300" },
  refunded: { label: "Refunded", cls: "bg-red-500/15 text-red-300" },
};

export default function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [following, setFollowing] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/bookings/mine"), api.get("/me/following")])
      .then(([b, f]) => { setBookings(b.data); setFollowing(f.data); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <div className="mb-8">
        <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Your collection</div>
        <h1 className="font-display text-3xl font-black uppercase text-zinc-50 sm:text-4xl">My tickets</h1>
      </div>

      {loading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-zinc-900" />
      ) : bookings.length === 0 ? (
        <div className="rounded-2xl border border-zinc-800 bg-[#3A1020] p-12 text-center">
          <Ticket className="mx-auto mb-3 h-10 w-10 text-amber-500" />
          <div className="font-display text-xl uppercase text-zinc-100">No tickets yet</div>
          <div className="mt-2 text-sm text-zinc-500">Grab your first one — the city is buzzing.</div>
          <Link to="/events" className="mt-5 inline-block rounded-full bg-amber-500 px-6 py-2 text-sm font-bold uppercase tracking-widest text-zinc-950 hover:bg-amber-400">Discover events</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {bookings.map((b) => {
            const st = STATUS[b.status];
            return (
              <Link key={b.id} to={`/bookings/${b.id}`} data-testid={`booking-item-${b.id}`}
                className={`group flex overflow-hidden rounded-2xl border border-zinc-800 bg-[#3A1020] transition hover:border-amber-500/40 ${st ? "opacity-80" : ""}`}>
                <div className="relative h-32 w-32 shrink-0 sm:h-36 sm:w-40">
                  <img src={posterSrc(b.event_poster_url)} alt={b.event_title} className={`h-full w-full object-cover ${st ? "grayscale" : ""}`} />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#3A1020]" />
                </div>
                <div className="flex flex-1 items-center justify-between gap-4 p-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-amber-400">{b.tier_name} × {b.quantity}{st && <span data-testid={`booking-status-${b.id}`} className={`rounded-full px-2 py-0.5 ${st.cls}`}>{st.label}</span>}</div>
                    <div className="mt-1 truncate font-display text-lg font-bold uppercase text-zinc-50">{b.event_title}</div>
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-zinc-400">
                      <span className="flex items-center gap-1"><Calendar className="h-3 w-3 text-amber-500" /><span className="font-mono">{formatDate(b.event_date)}</span></span>
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3 text-amber-500" />{b.event_venue}</span>
                    </div>
                  </div>
                  <div className="hidden shrink-0 text-right sm:block">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{b.booking_code}</div>
                    <div className="mt-1 font-mono text-lg font-bold text-amber-400">{formatCurrency(b.total)}</div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <div className="mt-12">
        <div className="mb-4 flex items-center gap-2"><Bell className="h-4 w-4 text-blush" /><h2 className="font-display text-xl font-extrabold text-zinc-50">Crews you follow</h2></div>
        {following.length === 0 ? (
          <div data-testid="following-empty" className="rounded-2xl border border-dashed border-zinc-800 p-6 text-sm text-zinc-500">Follow an organizer from any event page and we'll email you the moment they drop something new.</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {following.map((o) => (
              <Link key={o.id} to={`/organizers/${o.id}`} data-testid={`following-${o.id}`} className="rounded-2xl border border-zinc-800 bg-[#3A1020] p-4 transition hover:border-blush/50">
                <div className="font-display font-bold text-zinc-50">{o.name}</div>
                <div className="mt-1 text-xs text-zinc-500">{o.instagram_handle ? `@${o.instagram_handle} · ` : ""}{o.upcoming} upcoming</div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
